<template>
  <article class="admin-subscription-invoices-list" v-if="invoices.length > 0">
    <h1 class="mt-0">Frische Rechnungen</h1>
    <table>
      <thead>
        <tr>
          <th>Erstellt am</th>
          <th>Stripe Rechnung</th>
          <th>Stripe Abo</th>
          <th>simcision Abo</th>
          <th>Lexoffice Rechnung</th>
          <th>Sent At</th>
          <th>Fehler</th>
          <th>Aktionen</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="invoice in invoices" :key="invoice.id">
          <td>{{ formatDateString(invoice.created, "F") }}</td>
          <td>
            <a :href="`https://dashboard.stripe.com/invoices/${invoice.stripeInvoiceId}`" target="_blank">
              Stripe Rechnung
            </a>
          </td>
          <td>
            <a :href="`https://dashboard.stripe.com/subscriptions/${invoice.stripeSubscriptionId}`" target="_blank">
              Stripe Abo
            </a>
          </td>
          <td>
            <a :href="`/admin/users/${invoice.user}/subscriptions/${invoice.subscription}`" target="_blank">
              {{ invoice.subscription }}
            </a>
          </td>
          <td>
            <a v-if="invoice.lexofficeInvoiceId" :href="`https://app.lexware.de/permalink/invoices/view/${invoice.lexofficeInvoiceId}`" target="_blank">
              Lexoffice Rechnung
            </a>
          </td>
          <td>{{ invoice.sentAt ? formatDateString(invoice.sentAt, "F") : '-' }}</td>
          <td>
            <details v-if="invoice.lexofficeRequestedAt || invoice.lexofficeError">
              <summary v-if="invoice.lexofficeRequestedAt">
                🛑 Angefordert am {{ formatDateString(invoice.lexofficeRequestedAt, "F") }}
              </summary>
            
              <pre v-if="invoice.lexofficeError">{{ JSON.stringify(invoice.lexofficeError, null, 2) }}</pre>
            </details>
          </td>
          <td>
            <button class="button-icon" title="Löschen" @click="deleteInvoice(invoice.id)">
              <lucide-icon name="trash-2" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <pages-nav
      v-if="count > limit"
      class="mt-4"
      :limit="limit"
      :count="count"
      :offset="offset"
      @load="loadInvoices($event.offset)"
    />
  </article>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { formatDateString } from '@/i18n';
import { useAdmin } from "./useAdmin";

import LucideIcon from '@core/ui/LucideIcon.vue';
import PagesNav from './PagesNav.vue';

type SubscriptionInvoice = {
  id: number;
  stripeInvoiceId: string;
  stripeSubscriptionId: string;
  subscription: number | null;
  user: number | null;
  lexofficeInvoiceId: string | null;
  lexofficeDocumentId: string | null;
  lexofficeError: object | null;
  lexofficeRequestedAt: string | null;
  created: string;
  sentAt: string | null;
};

const adminAPI = useAdmin();

const loading = ref(false);
const limit = 5;
const count = ref(0);
const offset = ref(0);
const loadingError = ref<string | null>(null);
const invoices = ref<SubscriptionInvoice[]>([]);

const nextAvailable = ref(false);
const previousAvailable = ref(false);

async function loadInvoices(newOffset = 0) {
  loading.value = true;
  
  try {
    const response = await adminAPI.get<{
      offset: number;
      count: number;
      limit: number;
      results: SubscriptionInvoice[];
      next: string | null;
      previous: string | null;
    }>(`/subscription-invoices?offset=${newOffset}&limit=${limit}`, {
      parseResponse: response => response.body,
    });
    offset.value = response.offset;
    count.value = response.count;
    if (response.next) {
      nextAvailable.value = true;
    } else {
      nextAvailable.value = false;
    }
    if (response.previous) {
      previousAvailable.value = true;
    } else {
      previousAvailable.value = false;
    }
    invoices.value = response.results;
  } catch (error) {
    loadingError.value = (error as Error).message;
    console.error(error);
  } finally {
    loading.value = false;
  }
}

async function deleteInvoice(invoiceId: number) {
  if (!confirm('Rechnung wirklich löschen?')) {
    return;
  }

  try {
    await adminAPI.destroy(`/subscription-invoices/${invoiceId}`);
    invoices.value = invoices.value.filter(invoice => invoice.id !== invoiceId);
  } catch (error) {
    console.error(error);
  }
}

onMounted(() => {
  loadInvoices();
});
</script>

<style scoped>
.admin-subscription-invoices-list {
  padding: 1.5rem;
  box-shadow: inset 0 1px 0.5rem rgba(var(--tint-contrast), 0.2);
  border-radius: 1rem;
}
h1 {
  font-size: 1.5rem;
}
table {
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
}
tr:nth-child(even) {
  background-color: #f0f0f0;
}
td {
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-border);
}
th {
  padding: 0.5rem 1rem;
  border: 1px solid var(--color-border);
  background-color: var(--color-background-deep);
  color: var(--color-text-medium);
  text-align: left;
}
td, th {
  vertical-align: top;
}
</style>